import React, { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { LayoutDashboard, Users, Calendar, ClipboardList, Briefcase, LogOut, CalendarDays, FileBarChart, Settings, BookOpen, Menu, ChevronUp, ChevronDown, Info, ShieldAlert, Search, Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Brand } from '@/components/ui/Brand';
import { CommandPalette } from '@/components/CommandPalette';

const CLASSIFICATION_TOOLTIP = "This system is classified OFFICIAL (CLOSED) — UNCLASSIFIED under Singapore Government IM8 standards.\n\nYou may enter: Staff names, work emails, timetables, duty assignments, absence reasons.\n\nDo NOT enter: NRIC/FIN numbers, medical records, disciplinary records, or any classified MOE documents.";

const DashboardLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(true);

  useEffect(() => {
    const checkScroll = () => {
      const el = document.getElementById('main-content');
      if (!el) return;
      setShowScrollTop(el.scrollTop > 200);
      setShowScrollBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 200);
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const el = document.getElementById('main-content');
      if (el) {
        el.addEventListener('scroll', checkScroll, { passive: true });
        checkScroll();
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      const el = document.getElementById('main-content');
      if (el) el.removeEventListener('scroll', checkScroll);
    };
  }, [location.pathname]);

  const scrollToTop = () => {
    const el = document.getElementById('main-content');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const scrollToBottom = () => {
    const el = document.getElementById('main-content');
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'teacher', 'exco'] },
    { path: '/relief/today', label: 'Relief Today', icon: Zap, roles: ['admin', 'exco', 'hod'] },
    { path: '/absences', label: user?.role === 'admin' ? 'Absences' : 'My Absences', icon: Calendar, roles: ['admin', 'teacher'] },
    { path: '/relief', label: 'Relief Duties', icon: ClipboardList, roles: ['admin', 'teacher'] },
    { path: '/timetables', label: 'Timetables', icon: BookOpen, roles: ['admin', 'teacher'] },
    { path: '/exams', label: 'Exam Invigilation', icon: CalendarDays, roles: ['admin', 'teacher'] },
    { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'exco'] },
    { path: '/users', label: 'Users', icon: Users, roles: ['admin'] },
    { path: '/job-titles', label: 'Job Titles', icon: Briefcase, roles: ['admin'] },
    { path: '/configuration', label: 'Configuration', icon: Settings, roles: ['admin'] },
    { path: '/compliance', label: 'Compliance', icon: ShieldAlert, roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const SidebarContent = () => (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-6 border-b border-slate-200 shrink-0">
        <h1 className="text-xl font-bold tracking-tight"><Brand /></h1>
        <p className="text-xs text-slate-500 mt-1">Relief Teacher & Exam Management Tool</p>
      </div>

      <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <div
                className={`flex items-center px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-blue-900 border border-blue-100'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="mr-3 h-4 w-4" strokeWidth={1.5} />
                {item.label}
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-200 shrink-0">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
          className="w-full mb-3 flex items-center justify-between px-3 py-2 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-md border border-slate-200 transition-colors"
          data-testid="command-palette-trigger"
          title="Open command palette"
        >
          <span className="inline-flex items-center gap-2">
            <Search className="h-3.5 w-3.5" />
            Quick search...
          </span>
          <span className="text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white">Ctrl K</span>
        </button>
        <div className="mb-3 px-3 py-2 bg-slate-50 rounded-md">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Logged in as</p>
          <p className="text-sm font-medium text-slate-900 mt-1 truncate">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <span className="inline-block mt-2 px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-medium rounded">
            {user?.role}
          </span>
        </div>
        <Button
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-50"
          data-testid="logout-button"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Global command palette — Ctrl+K / Cmd+K */}
      <CommandPalette />
      {/* IM8 DP-8: Data Classification Banner */}
      <TooltipProvider delayDuration={200}>
        <div className="w-full bg-slate-800 text-center py-1 px-2 z-50 fixed top-0 left-0 right-0" data-testid="data-classification-banner">
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[10px] sm:text-xs font-semibold tracking-widest text-slate-300 uppercase cursor-help inline-flex items-center gap-1.5">
                Official (Closed) &mdash; Unclassified
                <Info className="h-3 w-3 text-slate-400" />
              </p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm text-left bg-slate-900 border-slate-700 text-slate-200 p-4" data-testid="classification-tooltip">
              <p className="text-xs font-semibold text-white mb-2">Data Classification: OFFICIAL (CLOSED)</p>
              <p className="text-[11px] leading-relaxed mb-2 text-slate-300">This system is classified under Singapore Government IM8 standards.</p>
              <p className="text-[11px] font-medium text-emerald-400 mb-1">Permitted data:</p>
              <p className="text-[11px] text-slate-300 mb-2">Staff names, work emails, timetables, duty assignments, absence reasons</p>
              <p className="text-[11px] font-medium text-red-400 mb-1">Do NOT enter:</p>
              <p className="text-[11px] text-slate-300">NRIC/FIN numbers, medical records, disciplinary records, or classified MOE documents</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-slate-200 sticky top-6 z-40" style={{ top: '24px' }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight"><Brand /></h1>
          <p className="text-xs text-slate-500">Relief Teacher & Exam Management Tool</p>
        </div>
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 flex flex-col">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 bg-white border-r border-slate-200 flex-col mt-6">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main id="main-content" className="flex-1 overflow-auto h-screen mt-6">
        <div className="p-4 sm:p-6 lg:p-8 xl:p-12">
          <Outlet />
        </div>
      </main>

      {/* Scroll Arrows */}
      <div className="fixed right-6 bottom-6 flex flex-col gap-2 z-50" data-testid="scroll-arrows">
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center hover:bg-slate-700 transition-all duration-200"
            data-testid="scroll-to-top"
            aria-label="Scroll to top"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
        )}
        {showScrollBottom && (
          <button
            onClick={scrollToBottom}
            className="w-10 h-10 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center hover:bg-slate-700 transition-all duration-200"
            data-testid="scroll-to-bottom"
            aria-label="Scroll to bottom"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DashboardLayout;